import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  ArrowDownCircle,
  ArrowUpCircle,
  Gauge,
  ListOrdered,
  Percent,
  TrendingDown,
  Wallet,
} from "lucide-react";
import { getFinanceCashflowReport } from "../../../api/reports/finance/financeReports";
import {
  showErrorToast,
  showSuccessToast,
} from "../../../components/ConfirmDialog";
import FinanceReportFilter from "./FinanceReportFilter";
import { CashflowCharts } from "./FinanceReportCharts";
import { exportCashflowExcel } from "./exportFinanceReportExcel";
import { exportCashflowPdf } from "./exportFinanceReportPdf";
import {
  Badge,
  DirectionBadge,
  EmptyState,
  EntryTypeBadge,
  MobileReportCard,
  MobileReportCardList,
  NetBadge,
  ReportPageHeader,
  SectionCard,
  SummaryStatCard,
  SummaryStatGrid,
  dateVN,
  money,
} from "./FinanceReportUi";
import {
  calcCashflowKpis,
  currentPeriodDefaults,
  dayCountInclusive,
  periodLabel,
} from "./financePeriodUtils";
import {
  ReportTablePagination,
  useReportTablePage,
} from "./useReportTablePage";

export default function FinanceCashflowReportPage() {
  const [filters, setFilters] = useState(currentPeriodDefaults);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        from_date: filters.from_date,
        to_date: filters.to_date,
      };
      if (filters.branch_id) params.branch_id = filters.branch_id;
      if (filters.direction) params.direction = filters.direction;
      if (filters.group) params.group = filters.group;
      const res = await getFinanceCashflowReport(params);
      setData(res);
    } catch (err) {
      showErrorToast(err?.message || "Không thể tải báo cáo dòng tiền");
    } finally {
      setLoading(false);
    }
  }, [
    filters.from_date,
    filters.to_date,
    filters.branch_id,
    filters.direction,
    filters.group,
  ]);

  useEffect(() => {
    load();
  }, [load]);

  const handleExportExcel = async () => {
    if (!data?.ledger?.length && !data?.summary) {
      showErrorToast("Không có dữ liệu để xuất");
      return;
    }
    setExporting(true);
    try {
      await exportCashflowExcel(data);
      showSuccessToast("Đã xuất Excel");
    } catch (err) {
      showErrorToast(err?.message || "Xuất Excel thất bại");
    } finally {
      setExporting(false);
    }
  };

  const handleExportPdf = async () => {
    if (!data?.ledger?.length && !data?.summary) {
      showErrorToast("Không có dữ liệu để xuất");
      return;
    }
    setExporting(true);
    try {
      await exportCashflowPdf(data);
      showSuccessToast("Đã xuất PDF");
    } catch (err) {
      showErrorToast(err?.message || "Xuất PDF thất bại");
    } finally {
      setExporting(false);
    }
  };

  const days = dayCountInclusive(filters.from_date, filters.to_date);
  const kpis = useMemo(
    () => calcCashflowKpis(data?.summary, data?.ledger || [], days),
    [data, days]
  );
  const ledgerCount = data?.ledger?.length || 0;
  const inCount = (data?.ledger || []).filter((r) => r.direction === "in").length;
  const outCount = ledgerCount - inCount;
  const ledgerPage = useReportTablePage(
    data?.ledger || [],
    `${filters.from_date}|${filters.to_date}|${filters.branch_id}|${filters.direction}|${filters.group}|${ledgerCount}`
  );

  return (
    <div className="space-y-3 sm:space-y-5 p-3 md:p-0 pb-4">
      <ReportPageHeader
        eyebrow="Báo cáo tài chính"
        title="Dòng tiền thu–chi"
        description="Phân tích cashflow theo năm/quý/tháng: tốc độ đốt tiền, khả năng phủ chi, net theo kỳ."
        scopeLabel={data?.scope?.scopeLabel}
        fromDate={filters.from_date}
        toDate={filters.to_date}
        extraBadges={[
          <Badge
            key="period"
            tone="red"
            className="!border-white/20 !bg-white/10 !text-white/90"
          >
            {periodLabel(filters)}
          </Badge>,
          <Badge
            key="rows"
            tone="red"
            className="!border-white/20 !bg-white/10 !text-white/90"
          >
            <ListOrdered size={11} /> {ledgerCount} dòng
          </Badge>,
        ]}
      />

      <FinanceReportFilter
        filters={filters}
        onChange={setFilters}
        onRefresh={load}
        onExportExcel={handleExportExcel}
        onExportPdf={handleExportPdf}
        exporting={exporting}
        loading={loading}
        canPickBranch={!!data?.scope?.canPickBranch}
        branchOptions={data?.branch_options || []}
      />

      <SummaryStatGrid>
        <SummaryStatCard
          label="Tổng thu"
          value={money(kpis.totalIn)}
          hint={`${inCount} dòng thu · ${days} ngày`}
          icon={ArrowDownCircle}
          tone="emerald"
          badge={<Badge tone="emerald">IN</Badge>}
        />
        <SummaryStatCard
          label="Tổng chi"
          value={money(kpis.totalOut)}
          hint={`${outCount} dòng chi`}
          icon={ArrowUpCircle}
          tone="red"
          badge={<Badge tone="red">OUT</Badge>}
        />
        <SummaryStatCard
          label="Net cashflow"
          value={money(kpis.net)}
          hint="Thu − Chi trong kỳ"
          icon={Wallet}
          tone="blue"
          badge={<NetBadge value={kpis.net} />}
        />
        <SummaryStatCard
          label="Burn ratio"
          value={`${kpis.burnRatio > 900 ? "∞" : kpis.burnRatio.toFixed(0)}%`}
          hint="Chi / Thu — >100% đang đốt vốn"
          icon={TrendingDown}
          tone={kpis.burnRatio > 100 ? "red" : "emerald"}
          badge={
            <Badge tone={kpis.burnRatio > 100 ? "red" : "emerald"}>
              {kpis.burnRatio > 100 ? "Cảnh báo" : "Ổn định"}
            </Badge>
          }
        />
        <SummaryStatCard
          label="Coverage"
          value={`${kpis.coverage.toFixed(2)}x`}
          hint="Thu / Chi — khả năng phủ chi phí"
          icon={Gauge}
          tone={kpis.coverage >= 1 ? "emerald" : "amber"}
          badge={
            <Badge tone={kpis.coverage >= 1 ? "emerald" : "amber"}>
              {kpis.coverage >= 1 ? "Đủ phủ" : "Thiếu phủ"}
            </Badge>
          }
        />
        <SummaryStatCard
          label="Net / ngày"
          value={money(kpis.avgDailyNet)}
          hint="Trung bình dòng tiền thuần mỗi ngày"
          icon={Percent}
          tone="blue"
          badge={<Badge tone="blue">Run-rate</Badge>}
        />
      </SummaryStatGrid>

      <CashflowCharts data={data} filters={filters} loading={loading} />

      {(data?.by_month || []).length > 1 && (
        <SectionCard title="Theo tháng" countLabel={`${data.by_month.length} tháng`}>
          <MobileReportCardList>
            {data.by_month.map((m) => (
              <MobileReportCard
                key={`m-${m.month}`}
                title={m.label}
                subtitle={`Thu ${money(m.total_in)} · Chi ${money(m.total_out)}`}
                amount={money(m.net)}
                amountTone={
                  Number(m.net) >= 0 ? "text-emerald-700" : "text-red-700"
                }
                badges={<NetBadge value={m.net} />}
              />
            ))}
          </MobileReportCardList>
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full text-left">
              <thead className="border-b border-gray-100 bg-gray-50/60">
                <tr>
                  <th className="p-4 text-[11px] font-black uppercase tracking-widest text-gray-400">
                    Tháng
                  </th>
                  <th className="p-4 text-right text-[11px] font-black uppercase tracking-widest text-gray-400">
                    Thu
                  </th>
                  <th className="p-4 text-right text-[11px] font-black uppercase tracking-widest text-gray-400">
                    Chi
                  </th>
                  <th className="p-4 text-right text-[11px] font-black uppercase tracking-widest text-gray-400">
                    Net
                  </th>
                  <th className="p-4 text-center text-[11px] font-black uppercase tracking-widest text-gray-400">
                    Trạng thái
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {data.by_month.map((m) => (
                  <tr key={m.month} className="hover:bg-gray-50/70">
                    <td className="p-4">
                      <Badge tone="slate">{m.label}</Badge>
                    </td>
                    <td className="p-4 text-right text-sm font-semibold text-emerald-700">
                      {money(m.total_in)}
                    </td>
                    <td className="p-4 text-right text-sm font-semibold text-red-700">
                      {money(m.total_out)}
                    </td>
                    <td className="p-4 text-right text-sm font-black text-gray-900">
                      {money(m.net)}
                    </td>
                    <td className="p-4 text-center">
                      <NetBadge value={m.net} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </SectionCard>
      )}

      <SectionCard
        title="Chi tiết sổ thu–chi"
        countLabel={`${ledgerCount} dòng`}
        action={
          <div className="flex gap-1.5">
            <Badge tone="emerald">{inCount} thu</Badge>
            <Badge tone="red">{outCount} chi</Badge>
          </div>
        }
      >
        {loading ? (
          <div className="p-10 text-center text-sm text-gray-400">Đang tải...</div>
        ) : !data?.ledger?.length ? (
          <EmptyState message="Không có dữ liệu trong kỳ" />
        ) : (
          <>
            <MobileReportCardList>
              {ledgerPage.pageRows.map((row) => {
                const isIn = row.direction === "in";
                return (
                  <MobileReportCard
                    key={`card-${row.entry_type}-${row.id}`}
                    title={row.code}
                    subtitle={row.description || dateVN(row.entry_date)}
                    amount={
                      isIn
                        ? money(row.amount_in)
                        : money(row.amount_out)
                    }
                    amountTone={isIn ? "text-emerald-700" : "text-red-700"}
                    badges={
                      <>
                        <DirectionBadge direction={row.direction} />
                        <EntryTypeBadge
                          entryType={row.entry_type}
                          label={row.entry_type_label}
                        />
                      </>
                    }
                    meta={`${dateVN(row.entry_date)}${
                      row.branch_name ? ` · ${row.branch_name}` : ""
                    }`}
                  />
                );
              })}
            </MobileReportCardList>
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full text-left">
                <thead className="border-b border-gray-100 bg-gray-50/60">
                  <tr>
                    <th className="p-4 text-[11px] font-black uppercase tracking-widest text-gray-400">
                      Ngày
                    </th>
                    <th className="p-4 text-[11px] font-black uppercase tracking-widest text-gray-400">
                      Chiều / Loại
                    </th>
                    <th className="p-4 text-[11px] font-black uppercase tracking-widest text-gray-400">
                      Mã
                    </th>
                    <th className="p-4 text-[11px] font-black uppercase tracking-widest text-gray-400">
                      Mô tả
                    </th>
                    <th className="p-4 text-[11px] font-black uppercase tracking-widest text-gray-400">
                      Chi nhánh
                    </th>
                    <th className="p-4 text-right text-[11px] font-black uppercase tracking-widest text-gray-400">
                      Thu
                    </th>
                    <th className="p-4 text-right text-[11px] font-black uppercase tracking-widest text-gray-400">
                      Chi
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {ledgerPage.pageRows.map((row) => (
                    <tr
                      key={`${row.entry_type}-${row.id}`}
                      className="hover:bg-gray-50/70"
                    >
                      <td className="p-4 text-sm font-medium text-gray-700">
                        {dateVN(row.entry_date)}
                      </td>
                      <td className="p-4">
                        <div className="flex flex-wrap gap-1.5">
                          <DirectionBadge direction={row.direction} />
                          <EntryTypeBadge
                            entryType={row.entry_type}
                            label={row.entry_type_label}
                          />
                        </div>
                      </td>
                      <td className="p-4">
                        <span className="rounded-lg bg-gray-50 px-2 py-1 font-mono text-xs font-bold text-gray-800">
                          {row.code}
                        </span>
                      </td>
                      <td className="max-w-xs truncate p-4 text-sm text-gray-600">
                        {row.description || "—"}
                      </td>
                      <td className="p-4">
                        {row.branch_name ? (
                          <Badge tone="slate">{row.branch_name}</Badge>
                        ) : (
                          <span className="text-sm text-gray-400">—</span>
                        )}
                      </td>
                      <td className="p-4 text-right text-sm font-bold text-emerald-700">
                        {row.amount_in ? money(row.amount_in) : (
                          <span className="font-normal text-gray-300">—</span>
                        )}
                      </td>
                      <td className="p-4 text-right text-sm font-bold text-red-700">
                        {row.amount_out ? money(row.amount_out) : (
                          <span className="font-normal text-gray-300">—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
        <ReportTablePagination
          show={ledgerPage.showPagination}
          total={ledgerPage.total}
          currentPage={ledgerPage.currentPage}
          pageSize={ledgerPage.pageSize}
          onPageChange={ledgerPage.setCurrentPage}
          onPageSizeChange={ledgerPage.setPageSize}
        />
      </SectionCard>
    </div>
  );
}
